import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../App";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  TrendingUp, AlertTriangle, CheckCircle2, Target, 
  ArrowRight, Lightbulb, Trophy
} from "lucide-react";

const SkillGapAnalysis = () => {
  const { user, token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [analysis, setAnalysis] = useState(null);

  useEffect(() => {
    if (user && token) {
      fetchAnalysis();
    }
  }, [user, token]);

  const fetchAnalysis = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/skills/gap-analysis`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAnalysis(response.data);
    } catch (error) {
      toast.error("Failed to load skill gap analysis");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </div>
    );
  }

  if (!analysis || Object.keys(analysis.current_skills).length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-3xl mx-auto">
            <Card>
              <CardHeader>
                <CardTitle>No Skill Data Yet</CardTitle>
                <CardDescription>
                  Take skill tests to generate your personalized gap analysis
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">
                  Complete at least one skill test to see your skill gap analysis and get personalized recommendations.
                </p>
                <Button onClick={() => navigate("/skill-tests")}>
                  Take Skill Tests
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <Navbar />
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold mb-2">Skill Gap Analysis</h1>
            <p className="text-gray-600">
              Personalized analysis for your career path: <span className="font-semibold">{analysis.target_career}</span>
            </p>
          </div>

          {/* Overall Readiness Card */}
          <Card className="mb-8 border-2 bg-gradient-to-br from-indigo-500 to-purple-600 text-white">
            <CardContent className="pt-6">
              <div className="grid md:grid-cols-2 gap-6">
                {/* Score Circle */}
                <div className="flex flex-col items-center justify-center">
                  <div className="relative w-40 h-40 mb-4">
                    <svg className="transform -rotate-90 w-40 h-40">
                      <circle
                        cx="80"
                        cy="80"
                        r="70"
                        stroke="currentColor"
                        strokeWidth="12"
                        fill="none"
                        className="text-white opacity-20"
                      />
                      <circle
                        cx="80"
                        cy="80"
                        r="70"
                        stroke="currentColor"
                        strokeWidth="12"
                        fill="none"
                        strokeDasharray={`${2 * Math.PI * 70}`}
                        strokeDashoffset={`${2 * Math.PI * 70 * (1 - analysis.overall_readiness / 100)}`}
                        className="text-white transition-all duration-1000"
                        strokeLinecap="round"
                      />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <div className="text-4xl font-bold">{Math.round(analysis.overall_readiness)}%</div>
                      <div className="text-sm opacity-90">Ready</div>
                    </div>
                  </div>
                  <h3 className="text-xl font-bold">Overall Career Readiness</h3>
                </div>

                {/* Message */}
                <div className="flex flex-col justify-center">
                  <div className="flex items-center gap-2 mb-3">
                    <Trophy className="w-6 h-6" />
                    <h3 className="text-2xl font-bold">Your Progress</h3>
                  </div>
                  <p className="text-lg opacity-90 mb-4">
                    {analysis.overall_readiness >= 80
                      ? "Excellent! You're well-prepared for your target career."
                      : analysis.overall_readiness >= 60
                      ? "Good progress! Focus on closing remaining gaps."
                      : "You're on your way. Keep building your skills!"}
                  </p>
                  <p className="text-sm opacity-80">
                    Based on {Object.keys(analysis.current_skills).length} skill test{Object.keys(analysis.current_skills).length !== 1 ? 's' : ''} completed
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Skills Comparison - Grouped by Category */}
          <div className="space-y-6 mb-8">
            {/* Technical Skills */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-blue-600" />
                  </div>
                  Technical Skills
                </CardTitle>
                <CardDescription>
                  Programming, development, and technical capabilities
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {Object.keys(analysis.required_skills).filter(skill => [
                    "Programming Languages", "Data Analysis", "Web Development", "Cloud Computing",
                    "Database Management", "DevOps", "Cybersecurity", "AI/ML",
                    "Mobile Development", "System Design", "Testing & QA", "Version Control"
                  ].includes(skill)).map((skill) => {
                    const currentLevel = analysis.current_skills[skill] || 0;
                    const requiredLevel = analysis.required_skills[skill];
                    const gap = analysis.gaps[skill];
                    const hasTested = skill in analysis.current_skills;
                    
                    return (
                      <div key={skill}>
                        <div className="flex justify-between items-center mb-2">
                          <h4 className="font-semibold text-sm">{skill}</h4>
                          <div className="flex gap-2 items-center">
                            {gap > 0 && (
                              <Badge variant="destructive" className="text-xs">
                                Gap: {Math.round(gap)}%
                              </Badge>
                            )}
                            {hasTested && currentLevel >= requiredLevel && (
                              <CheckCircle2 className="w-4 h-4 text-green-600" />
                            )}
                          </div>
                        </div>
                        
                        <div className="flex gap-4 items-center">
                          <div className="flex-1">
                            <div className="flex justify-between text-xs text-gray-600 mb-1">
                              <span>Current</span>
                              <span className="font-semibold">{Math.round(currentLevel)}%</span>
                            </div>
                            <Progress 
                              value={currentLevel} 
                              className={`h-2 ${!hasTested ? 'opacity-30' : ''}`}
                            />
                          </div>
                          
                          <div className="flex-1">
                            <div className="flex justify-between text-xs text-gray-600 mb-1">
                              <span>Required</span>
                              <span className="font-semibold">{Math.round(requiredLevel)}%</span>
                            </div>
                            <Progress value={requiredLevel} className="h-2 bg-gray-300" />
                          </div>
                        </div>
                        
                        {!hasTested && (
                          <p className="text-xs text-gray-500 mt-1">
                            Take the {skill} test to assess your level
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Soft Skills */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                    <Target className="w-5 h-5 text-green-600" />
                  </div>
                  Soft Skills
                </CardTitle>
                <CardDescription>
                  Communication, leadership, and interpersonal abilities
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {Object.keys(analysis.required_skills).filter(skill => [
                    "Communication", "Leadership", "Teamwork", "Problem Solving",
                    "Emotional Intelligence", "Adaptability", "Time Management",
                    "Negotiation", "Presentation", "Conflict Resolution", "Mentoring"
                  ].includes(skill)).map((skill) => {
                    const currentLevel = analysis.current_skills[skill] || 0;
                    const requiredLevel = analysis.required_skills[skill];
                    const gap = analysis.gaps[skill];
                    const hasTested = skill in analysis.current_skills;
                    
                    return (
                      <div key={skill}>
                        <div className="flex justify-between items-center mb-2">
                          <h4 className="font-semibold text-sm">{skill}</h4>
                          <div className="flex gap-2 items-center">
                            {gap > 0 && (
                              <Badge variant="destructive" className="text-xs">
                                Gap: {Math.round(gap)}%
                              </Badge>
                            )}
                            {hasTested && currentLevel >= requiredLevel && (
                              <CheckCircle2 className="w-4 h-4 text-green-600" />
                            )}
                          </div>
                        </div>
                        
                        <div className="flex gap-4 items-center">
                          <div className="flex-1">
                            <div className="flex justify-between text-xs text-gray-600 mb-1">
                              <span>Current</span>
                              <span className="font-semibold">{Math.round(currentLevel)}%</span>
                            </div>
                            <Progress 
                              value={currentLevel} 
                              className={`h-2 ${!hasTested ? 'opacity-30' : ''}`}
                            />
                          </div>
                          
                          <div className="flex-1">
                            <div className="flex justify-between text-xs text-gray-600 mb-1">
                              <span>Required</span>
                              <span className="font-semibold">{Math.round(requiredLevel)}%</span>
                            </div>
                            <Progress value={requiredLevel} className="h-2 bg-gray-300" />
                          </div>
                        </div>
                        
                        {!hasTested && (
                          <p className="text-xs text-gray-500 mt-1">
                            Take the {skill} test to assess your level
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Cognitive Abilities */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Target className="w-5 h-5 text-purple-600" />
                  </div>
                  Cognitive Abilities
                </CardTitle>
                <CardDescription>
                  Mental capabilities and thinking skills
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {Object.keys(analysis.required_skills).filter(skill => [
                    "Logical Reasoning", "Verbal Comprehension", "Spatial Reasoning",
                    "Numerical Ability", "Memory Retention", "Processing Speed", 
                    "Creative Thinking", "Critical Thinking"
                  ].includes(skill)).map((skill) => {
                    const currentLevel = analysis.current_skills[skill] || 0;
                    const requiredLevel = analysis.required_skills[skill];
                    const gap = analysis.gaps[skill];
                    const hasTested = skill in analysis.current_skills;
                    
                    return (
                      <div key={skill}>
                        <div className="flex justify-between items-center mb-2">
                          <h4 className="font-semibold text-sm">{skill}</h4>
                          <div className="flex gap-2 items-center">
                            {gap > 0 && (
                              <Badge variant="destructive" className="text-xs">
                                Gap: {Math.round(gap)}%
                              </Badge>
                            )}
                            {hasTested && currentLevel >= requiredLevel && (
                              <CheckCircle2 className="w-4 h-4 text-green-600" />
                            )}
                          </div>
                        </div>
                        
                        <div className="flex gap-4 items-center">
                          <div className="flex-1">
                            <div className="flex justify-between text-xs text-gray-600 mb-1">
                              <span>Current</span>
                              <span className="font-semibold">{Math.round(currentLevel)}%</span>
                            </div>
                            <Progress 
                              value={currentLevel} 
                              className={`h-2 ${!hasTested ? 'opacity-30' : ''}`}
                            />
                          </div>
                          
                          <div className="flex-1">
                            <div className="flex justify-between text-xs text-gray-600 mb-1">
                              <span>Required</span>
                              <span className="font-semibold">{Math.round(requiredLevel)}%</span>
                            </div>
                            <Progress value={requiredLevel} className="h-2 bg-gray-300" />
                          </div>
                        </div>
                        
                        {!hasTested && (
                          <p className="text-xs text-gray-500 mt-1">
                            Take the {skill} test to assess your level
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Other Skills */}
            {Object.keys(analysis.required_skills).filter(skill => 
              ["Domain Knowledge", "Decision Making"].includes(skill)
            ).length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                      <Target className="w-5 h-5 text-gray-600" />
                    </div>
                    Other Skills
                  </CardTitle>
                  <CardDescription>
                    Domain expertise and decision-making abilities
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {Object.keys(analysis.required_skills).filter(skill => [
                      "Domain Knowledge", "Decision Making"
                    ].includes(skill)).map((skill) => {
                      const currentLevel = analysis.current_skills[skill] || 0;
                      const requiredLevel = analysis.required_skills[skill];
                      const gap = analysis.gaps[skill];
                      const hasTested = skill in analysis.current_skills;
                      
                      return (
                        <div key={skill}>
                          <div className="flex justify-between items-center mb-2">
                            <h4 className="font-semibold text-sm">{skill}</h4>
                            <div className="flex gap-2 items-center">
                              {gap > 0 && (
                                <Badge variant="destructive" className="text-xs">
                                  Gap: {Math.round(gap)}%
                                </Badge>
                              )}
                              {hasTested && currentLevel >= requiredLevel && (
                                <CheckCircle2 className="w-4 h-4 text-green-600" />
                              )}
                            </div>
                          </div>
                          
                          <div className="flex gap-4 items-center">
                            <div className="flex-1">
                              <div className="flex justify-between text-xs text-gray-600 mb-1">
                                <span>Current</span>
                                <span className="font-semibold">{Math.round(currentLevel)}%</span>
                              </div>
                              <Progress 
                                value={currentLevel} 
                                className={`h-2 ${!hasTested ? 'opacity-30' : ''}`}
                              />
                            </div>
                            
                            <div className="flex-1">
                              <div className="flex justify-between text-xs text-gray-600 mb-1">
                                <span>Required</span>
                                <span className="font-semibold">{Math.round(requiredLevel)}%</span>
                              </div>
                              <Progress value={requiredLevel} className="h-2 bg-gray-300" />
                            </div>
                          </div>
                          
                          {!hasTested && (
                            <p className="text-xs text-gray-500 mt-1">
                              Take the {skill} test to assess your level
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Improvement Plan */}
          {analysis.improvement_plan && analysis.improvement_plan.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Lightbulb className="w-5 h-5 text-orange-600" />
                  Personalized Improvement Plan
                </CardTitle>
                <CardDescription>
                  Focus areas to bridge your skill gaps
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {analysis.improvement_plan.map((plan, idx) => {
                    const priorityColor = 
                      plan.priority === "High" ? "red" :
                      plan.priority === "Medium" ? "orange" : "blue";
                    
                    return (
                      <div 
                        key={idx}
                        className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg border-l-4"
                        style={{ borderLeftColor: `var(--${priorityColor}-500)` }}
                      >
                        <AlertTriangle className={`w-5 h-5 text-${priorityColor}-600 mt-0.5`} />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-semibold">{plan.skill}</h4>
                            <Badge 
                              variant={plan.priority === "High" ? "destructive" : "secondary"}
                              className="text-xs"
                            >
                              {plan.priority} Priority
                            </Badge>
                            <span className="text-sm text-gray-600">Gap: {plan.gap}%</span>
                          </div>
                          <p className="text-sm text-gray-600">{plan.recommendation}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Action Button */}
          <div className="mt-8 flex justify-center gap-4">
            <Button variant="outline" onClick={() => navigate("/skill-tests")}>
              Take More Tests
            </Button>
            <Button onClick={() => navigate("/skill-dna")}>
              View Your Skill DNA
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default SkillGapAnalysis;
